import { useEffect, useState, Fragment } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../../api/axios';

interface DetailItem {
  label: string;
  value: number;
  format: 'currency' | 'currency_2dp' | 'int' | 'ratio' | 'pct';
}

interface ContractRoleRow {
  role: string;
  contract_rate: number | null;
  inhouse_rate: number | null;
  premium: number | null;
}

interface YearPoint {
  year: number;
  status: 'yellow' | 'red' | 'none';
  value?: string;
  threshold?: string;
  threshold_label?: string;
  direction?: 'up' | 'down';
  raw_value?: number | null;
  raw_p70?: number | null;
  raw_p90?: number | null;
  raw_median?: number | null;
  raw_p100?: number | null;
  detail?: DetailItem[] | null;
  detail_tables?: { period: string; rows: ContractRoleRow[] }[] | null;
  has_rp?: boolean | null;
}

// Per-role contract-labor premium table. A role is highlighted amber/red when its
// premium clears the same-state peer p70/p90 — so "any role hitting" is visible.
function ContractRoleTable({ rows, p70, p90 }: {
  rows: ContractRoleRow[];
  p70?: number | null;
  p90?: number | null;
}) {
  const fmtRate = (v: number | null) => (v == null ? '—' : `$${v.toFixed(2)}`);
  const premiumColor = (p: number | null) => {
    if (p == null) return '#9CA3AF';
    if (p90 != null && p >= p90) return '#DC2626';
    if (p70 != null && p >= p70) return '#D97706';
    return '#374151';
  };
  const cell = { fontSize: '0.65rem', py: '2px', px: 0.75, lineHeight: 1.5 };

  // Flag rows whose rates look misfiled in the source cost report (HCRIS S-3
  // wage data is occasionally mis-keyed). A real agency premium is ~1.2–3×, and
  // nursing rates sit roughly $10–$200/hr; values outside that — or an in-house
  // rate that violates RN ≥ LPN ≥ CNA — indicate bad data, not a real signal.
  const inhouse: Record<string, number | null | undefined> = {};
  rows.forEach(r => { inhouse[r.role] = r.inhouse_rate; });
  const lt = (a: string, b: string) => {
    const x = inhouse[a], y = inhouse[b];
    return x != null && y != null && x < y;
  };
  const isSuspect = (r: ContractRoleRow) => {
    if (r.premium == null) return false;
    if (r.premium >= 5) return true;                                  // implausible premium
    if (r.contract_rate != null && r.contract_rate > 200) return true; // impossible contract rate
    if (r.inhouse_rate != null && (r.inhouse_rate > 200 || r.inhouse_rate < 8)) return true;
    if (r.role === 'RN' && (lt('RN', 'LPN') || lt('RN', 'CNA'))) return true;  // RN underpriced
    if (r.role === 'LPN' && lt('LPN', 'CNA')) return true;
    return false;
  };
  const anySuspect = rows.some(isSuspect);

  return (
    <Box sx={{ mt: 0.5, mb: 0.25, display: 'inline-block' }}>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', display: 'grid', gridTemplateColumns: 'auto auto auto auto' }}>
        {['Role', 'Contract $/hr', 'In-house $/hr', 'Premium'].map((h, i) => (
          <Box key={h} sx={{ ...cell, bgcolor: '#F3F4F6', fontWeight: 700, color: '#6B7280', textAlign: i === 0 ? 'left' : 'right' }}>{h}</Box>
        ))}
        {rows.map(r => {
          const suspect = isSuspect(r);
          const rowBg = suspect ? '#FEF3C7' : undefined;  // amber tint
          return (
          <Fragment key={r.role}>
            <Box sx={{ ...cell, fontWeight: 600, color: '#374151', bgcolor: rowBg }}>{r.role}</Box>
            <Box sx={{ ...cell, textAlign: 'right', color: '#6B7280', bgcolor: rowBg }}>{fmtRate(r.contract_rate)}</Box>
            <Box sx={{ ...cell, textAlign: 'right', color: '#6B7280', bgcolor: rowBg }}>{fmtRate(r.inhouse_rate)}</Box>
            <Box sx={{ ...cell, textAlign: 'right', fontWeight: 700, color: premiumColor(r.premium), bgcolor: rowBg, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
              {suspect && (
                <Tooltip arrow title="This role's rates look misfiled in the cost report (an implausible hourly rate, or RN reported below LPN/CNA). Treat the premium with caution.">
                  <WarningAmberIcon sx={{ fontSize: 12, color: '#B45309', cursor: 'help' }} />
                </Tooltip>
              )}
              {r.premium == null ? '—' : `${r.premium.toFixed(2)}×`}
            </Box>
          </Fragment>
          );
        })}
      </Box>
      {anySuspect && (
        <Typography sx={{ display: 'block', mt: 0.25, fontSize: '0.6rem', color: '#B45309', fontStyle: 'italic', maxWidth: 320 }}>
          ⚠ Highlighted rates appear misfiled in this year's cost report — the premium is likely overstated.
        </Typography>
      )}
    </Box>
  );
}

function formatDetailValue(item: DetailItem): string {
  switch (item.format) {
    case 'currency':
      return `$${Math.round(item.value).toLocaleString()}`;
    case 'currency_2dp':
      return `$${item.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'int':
      return Math.round(item.value).toLocaleString();
    case 'ratio':
      return item.value.toFixed(2);
    case 'pct':
      return `${item.value.toFixed(1)}%`;
    default:
      return String(item.value);
  }
}

interface Insight {
  signal_id: string;
  name: string;
  category: string;
  description?: string | null;
  source?: string | null;
  severity: 'yellow' | 'red';
  is_binary: boolean;
  unit?: string;
  years: YearPoint[];
}

interface Props {
  ccn: string;
  years?: number[];
}

// Format a raw value for bar chart labels
function fmtVal(v: number | null | undefined, unit?: string): string {
  if (v == null) return '';
  const abs = Math.abs(v);
  if (unit === '$/bed' || unit === '$/hr') return `$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (unit === '$/resident-day') return `$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (unit === '% change' || unit === '%') return `${abs.toFixed(1)}%`;
  if (unit === 'ratio') return abs.toFixed(2);
  if (unit === 'days') return `${abs.toFixed(1)}`;
  return abs.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

// Positioned label below a marker on the bar
function MarkerLabel({ leftPct, color, label, value }: {
  leftPct: number;
  color: string;
  label: string;
  value: string;
}) {
  return (
    <Box sx={{
      position: 'absolute', left: `${leftPct}%`, top: 0,
      transform: 'translateX(-50%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none',
    }}>
      <Typography sx={{ fontSize: '0.55rem', color, fontWeight: 700, lineHeight: 1 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.55rem', color: '#9CA3AF', lineHeight: 1, mt: '1px' }}>{value}</Typography>
    </Box>
  );
}

// Small horizontal bar chart showing facility value relative to benchmarks
function SignalBar({ y, unit, inverted }: {
  y: YearPoint;
  unit?: string;
  inverted?: boolean;
}) {
  const val = y.raw_value;
  const p70 = y.raw_p70;
  const p90 = y.raw_p90;
  const med = y.raw_median;

  if (val == null) return null;

  const isYoy = unit === '% change';
  const chartVal = isYoy ? Math.abs(val) : val;
  const chartP70 = p70 != null ? (isYoy ? Math.abs(p70) : p70) : null;
  const chartP90 = p90 != null ? (isYoy ? Math.abs(p90) : p90) : null;
  const chartMed = med != null ? (isYoy ? Math.abs(med) : med) : null;

  // Chart endpoint is anchored to the flag thresholds, not the state max.
  // p100 proved useless in practice because hospital-based distinct-part SNFs
  // report hospital-wide expenses on S-3 line 1, producing p100 values 3–13×
  // p90 — which compresses real facilities to the left of the bar. Anchoring
  // to p90 keeps the chart focused on the question that actually matters:
  // "where is this facility relative to the line that got it flagged?"
  //
  // Non-inverted: maxVal = max(p90 × 1.2, facility × 1.05) → facility
  //   flagged red sits at ~83–95% of the bar; green facility sits below p70
  //   with thresholds clearly visible to the right.
  // Inverted: no threshold anchoring — p90/p70 are on the "good" side of the
  //   facility, so we fall back to padded max-of-values.
  const allVals = [chartVal, chartP70, chartP90, chartMed].filter((v): v is number => v != null);
  let maxVal: number;
  if (!inverted && chartP90 != null) {
    maxVal = Math.max(chartP90 * 1.2, chartVal * 1.05);
  } else if (!inverted && chartP70 != null) {
    maxVal = Math.max(chartP70 * 1.3, chartVal * 1.05);
  } else {
    maxVal = Math.max(...allVals) * 1.3;
  }
  if (maxVal <= 0) return null;

  const pct = (v: number) => Math.min((v / maxVal) * 100, 100);

  const barColor = y.status === 'red' ? '#FCA5A5' : y.status === 'yellow' ? '#FDE68A' : '#D1D5DB';
  const edgeColor = y.status === 'red' ? '#DC2626' : y.status === 'yellow' ? '#D97706' : '#6B7280';

  // Collect benchmark markers sorted by position
  const markers: { pctLeft: number; color: string; label: string; value: string }[] = [];
  if (chartMed != null) markers.push({ pctLeft: pct(chartMed), color: '#9CA3AF', label: 'med', value: fmtVal(chartMed, unit) });
  if (chartP70 != null) markers.push({ pctLeft: pct(chartP70), color: '#D97706', label: inverted ? 'p30' : 'p70', value: fmtVal(chartP70, unit) });
  if (chartP90 != null) markers.push({ pctLeft: pct(chartP90), color: '#DC2626', label: inverted ? 'p10' : 'p90', value: fmtVal(chartP90, unit) });
  markers.sort((a, b) => a.pctLeft - b.pctLeft);

  // Stagger labels to alternating rows when markers are close (<12% apart)
  const labelRows: number[] = [];
  for (let i = 0; i < markers.length; i++) {
    if (i === 0) { labelRows.push(0); continue; }
    const gap = markers[i].pctLeft - markers[i - 1].pctLeft;
    labelRows.push(gap < 12 && labelRows[i - 1] === 0 ? 1 : 0);
  }

  const TRACK_H = 16;
  const TRACK_TOP = 10; // leave room for "Facility" label above
  const LABEL_AREA = 24; // two possible label rows

  return (
    <Box sx={{ position: 'relative', width: '100%', height: TRACK_TOP + TRACK_H + LABEL_AREA, mt: 0.5 }}>
      {/* "Facility" label above edge */}
      <Box sx={{
        position: 'absolute', left: `${pct(chartVal)}%`, top: 0, zIndex: 4,
        transform: 'translateX(-50%)', whiteSpace: 'nowrap', pointerEvents: 'none',
      }}>
        <Typography sx={{ fontSize: '0.55rem', color: edgeColor, fontWeight: 700, lineHeight: 1, userSelect: 'none' }}>
          Facility
        </Typography>
      </Box>

      {/* Gray track */}
      <Box sx={{ position: 'absolute', left: 0, right: 0, top: TRACK_TOP, height: TRACK_H, bgcolor: '#F3F4F6', borderRadius: 0.5 }} />
      {/* Fill */}
      <Box sx={{ position: 'absolute', left: 0, top: TRACK_TOP, height: TRACK_H, width: `${pct(chartVal)}%`, bgcolor: barColor, borderRadius: 0.5 }} />
      {/* Facility edge — thick */}
      <Box sx={{ position: 'absolute', left: `${pct(chartVal)}%`, top: TRACK_TOP - 2, height: TRACK_H + 4, width: 4, bgcolor: edgeColor, borderRadius: 0.5, ml: '-2px', zIndex: 3 }} />

      {/* Benchmark tick lines */}
      {markers.map(m => (
        <Box key={m.label} sx={{ position: 'absolute', left: `${m.pctLeft}%`, top: TRACK_TOP, height: TRACK_H, width: 1.5, bgcolor: m.color, ml: '-0.75px', zIndex: 2 }} />
      ))}

      {/* Benchmark labels below bar — staggered when close */}
      {markers.map((m, i) => (
        <Box key={m.label} sx={{ position: 'absolute', left: 0, right: 0, top: TRACK_TOP + TRACK_H + 1 + labelRows[i] * 12 }}>
          <MarkerLabel leftPct={m.pctLeft} color={m.color} label={m.label} value={m.value} />
        </Box>
      ))}
    </Box>
  );
}

// Legend for bar chart markers
function ChartLegend() {
  return (
    <Box sx={{ display: 'flex', gap: 2.5, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {/* Facility: bar + thick edge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ position: 'relative', width: 24, height: 12 }}>
          <Box sx={{ width: 20, height: 10, bgcolor: '#FDE68A', borderRadius: 0.5, position: 'absolute', left: 0, top: 1 }} />
          <Box sx={{ width: 4, height: 12, bgcolor: '#D97706', borderRadius: 0.5, position: 'absolute', right: 0, top: 0 }} />
        </Box>
        <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.65rem' }}>Facility</Typography>
      </Box>
      {/* Benchmark ticks */}
      {[
        { color: '#9CA3AF', label: 'Median' },
        { color: '#D97706', label: '70th pctile' },
        { color: '#DC2626', label: '90th pctile' },
      ].map(item => (
        <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 1.5, height: 12, bgcolor: item.color, borderRadius: 0.5 }} />
          <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.65rem' }}>{item.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// Signals with inverted thresholds (lower = worse)
const INVERTED_SIGNALS = new Set(['rn_lpn_wage', 'low_rn_rate', 'low_dietary']);

export default function ErnieInsights({ ccn, years }: Props) {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (years?.length) {
      params.years = years.join(',');
    }
    api.get(`/facilities/${ccn}/insights/`, { params })
      .then(res => setInsights(res.data))
      .catch(() => setError('Failed to load insights'))
      .finally(() => setLoading(false));
  }, [ccn, years?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (insights.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No flagged signals for this facility. All metrics are within normal ranges.
        </Typography>
      </Paper>
    );
  }

  // Check if any non-binary insight has raw values (for showing legend)
  const hasChartData = insights.some(i => !i.is_binary && i.years.some(y => y.raw_value != null));

  // Group by category preserving backend order
  const grouped: { category: string; items: Insight[] }[] = [];
  for (const insight of insights) {
    const last = grouped[grouped.length - 1];
    if (last && last.category === insight.category) {
      last.items.push(insight);
    } else {
      grouped.push({ category: insight.category, items: [insight] });
    }
  }

  const redCount = insights.filter(i => i.severity === 'red').length;
  const yellowCount = insights.filter(i => i.severity === 'yellow').length;
  const scrollToSignal = (signalId: string) => {
    document.getElementById(`insight-${signalId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const summaryBlock = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="body2" sx={{ color: '#102B3D', fontWeight: 600 }}>
          {insights.length} area{insights.length !== 1 ? 's' : ''} flagged
          {redCount > 0 || yellowCount > 0 ? (
            <> — <b style={{ color: '#DC2626' }}>{redCount} red</b>, <b style={{ color: '#D97706' }}>{yellowCount} yellow</b></>
          ) : null}
        </Typography>
        <Tooltip
          arrow
          title={
            <Box sx={{ fontSize: '0.72rem', lineHeight: 1.5, p: 0.25 }}>
              <Box sx={{ fontWeight: 700, mb: 0.5 }}>
                How signals are flagged
              </Box>
              <Box component="ul" sx={{ m: 0, pl: 1.75, '& li': { mb: 0.5 }, '& li:last-child': { mb: 0 } }}>
                <li>Computed from cost report data, compared to same-state peers in the same year.</li>
                <li><b style={{ color: '#FDE68A' }}>Yellow</b> = above the 70th percentile of peers.</li>
                <li><b style={{ color: '#FCA5A5' }}>Red</b> = above the 90th percentile.</li>
                <li>Bar charts are scaled so the thresholds stay visible — a facility near the right edge is at or above the 90th-percentile line.</li>
              </Box>
            </Box>
          }
        >
          <InfoOutlinedIcon sx={{ fontSize: 15, color: '#9CA3AF', cursor: 'help' }} />
        </Tooltip>
      </Box>
      {hasChartData && <ChartLegend />}
    </>
  );

  return (
    <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
      {/* ── Left: sticky summary + signal nav ── */}
      <Box
        sx={{
          width: 300,
          minWidth: 260,
          flexShrink: 0,
          position: 'sticky',
          top: 16,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          display: { xs: 'none', md: 'block' },
          pr: 1,
        }}
      >
        {summaryBlock}
        <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
          {grouped.map(group => (
            <Box key={group.category} sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: '#9CA3AF', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}
              >
                {group.category}
              </Typography>
              {group.items.map(insight => (
                <Box
                  key={insight.signal_id}
                  onClick={() => scrollToSignal(insight.signal_id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    py: 0.4, px: 0.75, ml: -0.75, borderRadius: 1, cursor: 'pointer',
                    '&:hover': { bgcolor: '#EAF1F5' },
                  }}
                >
                  <Box sx={{
                    width: 7, height: 7, minWidth: 7, borderRadius: '50%',
                    bgcolor: insight.severity === 'red' ? '#DC2626' : '#D97706',
                  }} />
                  <Typography variant="caption" sx={{ color: '#374151', lineHeight: 1.3 }}>
                    {insight.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Right: the insights ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>{summaryBlock}</Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
        {grouped.map((group, gi) => (
          <Box key={group.category} sx={gi > 0 ? { borderTop: '1px solid', borderColor: 'divider', pt: 3.5 } : undefined}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                color: '#6B7280',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                letterSpacing: '0.08em',
                mb: 1.5,
                display: 'block',
              }}
            >
              {group.category}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {group.items.map(insight => {
                const inverted = INVERTED_SIGNALS.has(insight.signal_id);
                return (
                  <Box
                    key={insight.signal_id}
                    id={`insight-${insight.signal_id}`}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      scrollMarginTop: 88,
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      px: 2.5,
                      py: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        minWidth: 7,
                        height: 7,
                        borderRadius: '50%',
                        mt: 0.8,
                        bgcolor: insight.severity === 'red' ? '#DC2626' : '#D97706',
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#1F2937', mb: insight.description ? 0.25 : 0.5 }}>
                        {insight.name}
                      </Typography>

                      {insight.description && (
                        <Typography variant="caption" sx={{ color: '#4B5563', display: 'block', mb: insight.source ? 0.25 : 0.75, lineHeight: 1.45 }}>
                          {insight.description}
                        </Typography>
                      )}

                      {insight.source && (
                        <Typography
                          variant="caption"
                          sx={{ color: '#6B7280', display: 'block', mb: 0.75, fontSize: '0.75rem', fontStyle: 'italic' }}
                        >
                          Source: {insight.source}
                        </Typography>
                      )}

                      {insight.is_binary ? (
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 0.25 }}>
                            {(() => {
                              const flagged = insight.years.filter(y => y.status !== 'none');
                              if (flagged.length === 0) return 'Flagged in —';
                              if (insight.signal_id === 'cr_vs_pbj') {
                                const pctOf = (y: YearPoint) => (y.raw_value != null ? Math.round((y.raw_value - 1) * 100) : null);
                                if (flagged.length === 1) {
                                  const p = pctOf(flagged[0]);
                                  return `Flagged in ${flagged[0].year}`
                                    + (p != null ? ` — the Cost Report reported ${p}% more RN hours than PBJ` : '');
                                }
                                const parts = flagged.map(y => {
                                  const p = pctOf(y);
                                  return p != null ? `${y.year} (+${p}%)` : `${y.year}`;
                                });
                                return `Flagged in ${parts.join(', ')} — Cost Report RN hours above PBJ`;
                              }
                              return 'Flagged in ' + flagged.map(y => y.year).join(', ');
                            })()}
                          </Typography>
                          {insight.years
                            .filter(y => y.detail && y.detail.length > 0 && y.status !== 'none')
                            .map(y => (
                              <Typography
                                key={y.year}
                                variant="caption"
                                sx={{ color: '#6B7280', display: 'block', fontSize: '0.65rem' }}
                              >
                                <b style={{ fontWeight: 600, color: '#374151' }}>{y.year}:</b>{' '}
                                {y.detail!.map((item, i) => (
                                  <span key={item.label}>
                                    {i > 0 && ' · '}
                                    {item.label}: <b style={{ fontWeight: 600, color: '#374151' }}>{formatDetailValue(item)}</b>
                                  </span>
                                ))}
                              </Typography>
                            ))}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {insight.years.map(y => {
                            const rpSuppressed =
                              y.status === 'none'
                              && y.has_rp === true
                              && insight.signal_id.startsWith('hidden_rp_');
                            return (
                            <Box
                              key={y.year}
                              sx={{
                                bgcolor: '#F8FAFC',
                                border: '1px solid #EEF2F6',
                                borderRadius: 1.5,
                                px: 1.5,
                                py: 1.25,
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: y.status === 'red' ? '#DC2626' : y.status === 'yellow' ? '#92400E' : '#6B7280', fontWeight: 600 }}
                                >
                                  {y.year}
                                </Typography>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="caption" sx={{ color: '#6B7280', whiteSpace: 'nowrap', display: 'block', fontWeight: 600 }}>
                                    {y.direction ? `${y.direction === 'up' ? '↑' : '↓'} ` : ''}
                                    {y.value}
                                  </Typography>
                                  {(() => {
                                    // Plain-English gloss for the bare ratio signals
                                    if (y.raw_value == null) return null;
                                    let gloss: string | null = null;
                                    if (insight.signal_id === 'rn_lpn_wage') {
                                      const pct = Math.round(Math.abs(y.raw_value - 1) * 100);
                                      gloss = `RN paid ${pct}% ${y.raw_value >= 1 ? 'more' : 'less'}/hr than LPN`;
                                    } else if (insight.signal_id === 'contract_labor') {
                                      const pct = Math.round((y.raw_value - 1) * 100);
                                      if (pct > 0) gloss = `contract staff paid ${pct}% more/hr than in-house`;
                                    }
                                    return gloss && (
                                      <Typography sx={{ fontSize: '0.6rem', color: '#9CA3AF', whiteSpace: 'nowrap', display: 'block', lineHeight: 1.3 }}>
                                        {gloss}
                                      </Typography>
                                    );
                                  })()}
                                </Box>
                              </Box>
                              {y.detail_tables && y.detail_tables.length > 0 ? (
                                /* Tables left (fixed width so charts align across years),
                                   bar chart filling the space to the right */
                                <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                  <Box sx={{ flex: '0 0 480px', maxWidth: '100%' }}>
                                    {y.detail_tables.map((t) => (
                                      <Box key={t.period} sx={{ display: 'block' }}>
                                        {y.detail_tables!.length > 1 && (
                                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#6B7280', mt: 0.75 }}>
                                            {t.period}
                                          </Typography>
                                        )}
                                        <ContractRoleTable rows={t.rows} p70={y.raw_p70} p90={y.raw_p90} />
                                      </Box>
                                    ))}
                                    {y.detail_tables.length > 1 && (
                                      <Typography sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem', color: '#6B7280', fontStyle: 'italic', maxWidth: 400 }}>
                                        This year spans {y.detail_tables.length} cost reports (mid-year ownership change);
                                        each table shows that filing's as-filed rates. The year's headline value is the
                                        screening ratio — the latest filing's contract cost per hour over the cleaned
                                        annual in-house wage — so it can differ slightly from the table premiums.
                                      </Typography>
                                    )}
                                  </Box>
                                  {y.raw_value != null && (
                                    <Box sx={{ flex: 1, minWidth: 320, pb: 0.5 }}>
                                      <SignalBar y={y} unit={insight.unit} inverted={inverted} />
                                    </Box>
                                  )}
                                </Box>
                              ) : (
                                <>
                                  {y.detail && y.detail.length > 0 && (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: '#6B7280', display: 'block', fontSize: '0.65rem', mt: 0.25 }}
                                    >
                                      {y.detail.map((item, i) => (
                                        <span key={item.label}>
                                          {i > 0 && ' · '}
                                          {item.label}: <b style={{ fontWeight: 600, color: '#374151' }}>{formatDetailValue(item)}</b>
                                        </span>
                                      ))}
                                    </Typography>
                                  )}
                                  {y.raw_value != null && (
                                    <SignalBar y={y} unit={insight.unit} inverted={inverted} />
                                  )}
                                </>
                              )}
                              {rpSuppressed && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#6B7280', display: 'block', fontSize: '0.65rem', mt: 0.5, fontStyle: 'italic', lineHeight: 1.4 }}
                                >
                                  Not flagged: a related party is disclosed on Worksheet A-8-1 this year, so the arrangement isn't <i>hidden</i> — even though the cost is elevated.
                                </Typography>
                              )}
                            </Box>
                          );
                          })}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
      </Box>
    </Box>
  );
}
